import { useRef, useState } from 'react';
import { CheckCircle, Loader2, Upload, X } from 'lucide-react';
import { useApplyForJob } from '../../hooks/useJobBoard';
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaCheckbox from '../RecaptchaCheckbox';

interface Props {
  jobId: string;
  onClose?: () => void;
  colors?: {
    primaryColor?: string;
    secondaryColor?: string;
    buttonColor?: string;
    accentColor?: string;
    headingTextColor?: string;
    bodyTextColor?: string;
    mutedTextColor?: string;
  };
  candidateDisclaimer?: string;
  privacyPolicyUrl?: string;
}
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default function JobApplicationForm({ jobId, onClose, colors, candidateDisclaimer, privacyPolicyUrl }: Props) {
  const apply = useApplyForJob();
  const [values, setValues] = useState({ firstName: '', lastName: '', emailAddress: '', contactNumber: '', coverLetter: '' });
  const [resume, setResume] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState('');
  
  // const primaryColor = colors?.primaryColor || "#172554";
  const secondaryColor = colors?.secondaryColor || "#e2e8f0";
  const buttonColor = colors?.buttonColor || "#7f1d1d";
  const accentColor = colors?.accentColor || "#be123c";
  const headingTextColor = colors?.headingTextColor || "#0f172a";
  const bodyTextColor = colors?.bodyTextColor || "#475569";
  const mutedTextColor = colors?.mutedTextColor || "#94a3b8";

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const setValue = (field: keyof typeof values, value: string) => setValues((current) => ({ ...current, [field]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (apply.isPending) return;
    const nextErrors: Record<string, string> = {};
    if (!values.firstName.trim()) nextErrors.firstName = 'First name is required';
    if (!values.lastName.trim()) nextErrors.lastName = 'Last name is required';
    if (!values.emailAddress.trim()) {
      nextErrors.emailAddress =
        'Email address is required';
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        values.emailAddress.trim()
      )
    ) {
      nextErrors.emailAddress =
        'Please enter a valid email address';
    }
    if (!resume) nextErrors.resume = 'Resume is required';
    else if (!['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(resume.type) && !/\.(pdf|doc|docx)$/i.test(resume.name)) nextErrors.resume = 'Use a PDF, DOC, or DOCX file';
    else if (resume.size > MAX_FILE_SIZE) nextErrors.resume = 'Resume must be 10 MB or smaller';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification.');
      return;
    }
    setCaptchaError('');
    const form = new FormData();
    form.append('JobId', jobId); form.append('FirstName', values.firstName); form.append('LastName', values.lastName); form.append('EmailAddress', values.emailAddress); form.append('ContactNumber', values.contactNumber); form.append('CoverLetter', values.coverLetter); form.append('Resume', resume as File); form.append('captchaToken', captchaToken);
    apply.mutate(form, {
      onSuccess: () => setSubmitted(true),
      onSettled: () => {
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      },
    });
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-8 text-center">
        <CheckCircle
          className="mx-auto text-emerald-600"
          size={44}
        />

        <h2
          className="mt-4 text-xl font-bold"
          style={{ color: headingTextColor }}
        >
          Application Submitted
        </h2>

        <p
          className="mt-2 text-sm"
          style={{ color: bodyTextColor }}
        >
          Thank you for your interest. The hiring team will
          review your application and contact you if your
          qualifications match the position.
        </p>

        {onClose && (
          <button
            onClick={onClose}
            style={{
              backgroundColor: buttonColor,
            }}
            className="mt-6 rounded-xl px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  const inputClass = "input-base";

  return (
    <form
      onSubmit={submit}
      className="relative space-y-5 text-left"
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-0 top-0 rounded-lg p-2 transition hover:bg-slate-100"
          style={{
            color: headingTextColor,
            backgroundColor: secondaryColor,
          }}
        >
          <X size={18} />
        </button>
      )}

      <div
        className="mb-6 border-b pb-4"
        style={{
          borderColor: secondaryColor,
        }}
      >
        <h3
          className="text-xl font-bold"
          style={{
            color: headingTextColor,
          }}
        >
          Application Details
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {(["firstName", "lastName"] as const).map(
          (field) => (
            <div key={field}>
              <label className="mb-1 block text-sm font-semibold" style={{color: bodyTextColor}}>
                {field === "firstName"
                  ? "First Name"
                  : "Last Name"}{" "}
                *
              </label>

              <input
                className={inputClass}
                style={{
                  color: bodyTextColor,
                  borderColor: secondaryColor,
                }}
                value={values[field]}
                onChange={(e) =>
                  setValue(field, e.target.value)
                }
              />

              {errors[field] && (
                <p className="mt-1 text-xs text-red-600">
                  {errors[field]}
                </p>
              )}
            </div>
          )
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold" style={{color: bodyTextColor}}>
          Email Address *
        </label>

        <input
          type="email"
          className={inputClass}
          style={{
            color: bodyTextColor,
            borderColor: secondaryColor,
          }}
          value={values.emailAddress}
          onChange={(e) =>
            setValue("emailAddress", e.target.value)
          }
        />

        {errors.emailAddress && (
          <p className="mt-1 text-xs text-red-600">
            {errors.emailAddress}
          </p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold" style={{color: bodyTextColor}}>
          Contact Number
        </label>

        <input
          type="tel"
          className={inputClass}
          style={{
            color: bodyTextColor,
            borderColor: secondaryColor,
          }}
          value={values.contactNumber}
          onChange={(e) =>
            setValue("contactNumber", e.target.value)
          }
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold" style={{color: bodyTextColor}}>
          Cover Letter
        </label>

        <textarea
          rows={4}
          className={inputClass}
          style={{
            color: bodyTextColor,
            borderColor: secondaryColor,
          }}
          value={values.coverLetter}
          onChange={(e) =>
            setValue("coverLetter", e.target.value)
          }
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold text-slate-700">
          Resume *
          <span
            className="ml-1 font-normal"
            style={{
              color: mutedTextColor,
            }}
          >
            PDF, DOC, DOCX, up to 10 MB
          </span>
        </label>

        <label
          className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed bg-slate-50 px-4 py-4 text-sm transition"
          style={{
            borderColor: secondaryColor,
            color: mutedTextColor,
          }}
        >
          <Upload size={18} />

          <span className="flex-1 truncate">
            {resume?.name ??
              "Choose a resume file"}
          </span>

          {resume && (
            <span
              className="rounded-full px-2 py-1 text-xs font-semibold"
              style={{
                backgroundColor: `${accentColor}20`,
                color: accentColor,
              }}
            >
              Selected
            </span>
          )}

          <input
            type="file"
            className="sr-only"
            accept=".pdf,.doc,.docx"
            onChange={(e) =>
              setResume(
                e.target.files?.[0] ?? null
              )
            }
          />
        </label>

        {errors.resume && (
          <p className="mt-1 text-xs text-red-600">
            {errors.resume}
          </p>
        )}
      </div>
      {candidateDisclaimer && (
        <div
          className="rounded-xl border bg-slate-50 p-4 text-xs leading-5"
          style={{
            borderColor: secondaryColor,
            color: bodyTextColor,
          }}
        >
          <p
            className="mb-2 font-semibold"
            style={{
              color: headingTextColor,
            }}
          >
            Application Disclaimer
          </p>

          <p className="whitespace-pre-wrap">
            {candidateDisclaimer}
          </p>

          {privacyPolicyUrl && (
            <p className="mt-3">
              For more information about how your personal information is handled, please review the{" "}
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
                style={{
                  color: accentColor,
                }}
              >
                Privacy Policy
              </a>
              .
            </p>
          )}
        </div>
      )}

      <div>
        <RecaptchaCheckbox ref={recaptchaRef} onChange={(token) => { setCaptchaToken(token); setCaptchaError(''); }} disabled={apply.isPending} />
        {captchaError && <p className="mt-2 text-sm text-red-600">{captchaError}</p>}
      </div>

      <button
        type="submit"
        disabled={apply.isPending}
        style={{
          backgroundColor: buttonColor,
        }}
        className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
      >
        {apply.isPending && (
          <Loader2
            size={18}
            className="animate-spin"
          />
        )}

        Submit Application
      </button>
    </form>
  );
}